/**
 * API Client — single entry point for all backend requests.
 *
 * - Base URL: REACT_APP_API_URL or auto-detect (localhost → :5001, prod → api.<host>)
 * - JSON: Content-Type application/json, body auto-stringified
 * - Token: Bearer from localStorage (auth_token), auto-expiry check
 * - Errors: throws Error with message from { error.message } or { message }
 *
 * Usage: import { get, post, put, del } from "../utils/api";
 */

import { isTokenExpired } from "./jwt";

/** Storage key for auth token (shared with AuthContext) */
export const AUTH_STORAGE_KEY = "auth_token";
const REFRESH_KEY = "prombiz_refresh_token";

/** Refresh lock to prevent concurrent refresh attempts */
let isRefreshing = false;
let refreshPromise = null;

/**
 * Content language for **public** multilingual API (`?lang=`) — aligned with URL locale + localStorage.
 * Same key as interface language storage so Explore/profile match `/en/...` vs `/ru/...`.
 */
const LANG_STORAGE_KEY = "prombiz_lang";
const getContentLang = () => {
  if (typeof window === "undefined") return "en";
  const stored = localStorage.getItem(LANG_STORAGE_KEY);
  return ["en", "ru", "he"].includes(stored) ? stored : "en";
};

/** Append `lang` to GET /public/businesses* and /public/blog* when missing (explicit content_language for CDN/logs). */
function withPublicContentLangQuery(endpoint, method) {
  if (String(method || "GET").toUpperCase() !== "GET" || typeof endpoint !== "string") return endpoint;
  const basePath = endpoint.split("?")[0];
  if (!/^\/public\/(businesses|blog)/.test(basePath)) return endpoint;
  if (/[?&]lang=/.test(endpoint)) return endpoint;
  const lang = getContentLang();
  return `${endpoint}${endpoint.includes("?") ? "&" : "?"}lang=${encodeURIComponent(lang)}`;
}

/** Event dispatched when API returns 401 — AuthContext listens and clears auth */
export const AUTH_SESSION_EXPIRED_EVENT = "auth:session-expired";

/** Base URL from env or hostname */
function getApiBaseUrl() {
  const url = process.env.REACT_APP_API_URL;
  if (url) return url.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location) {
    let host = window.location.hostname;
    // Strip www prefix for API subdomain
    if (host.startsWith("www.")) host = host.slice(4);
    const base = host === "localhost" ? "http://localhost:5001" : `https://api.${host}`;
    return `${base}/api`;
  }
  return "/api";
}

const API_BASE_URL = getApiBaseUrl();

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  const token = localStorage.getItem(AUTH_STORAGE_KEY);

  // If token is expired but refresh token exists, return the expired token
  // so the request fires and the 401 interceptor can handle refresh
  if (token && isTokenExpired(token)) {
    const rt = localStorage.getItem(REFRESH_KEY);
    if (rt) return token;
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }

  return token;
};

/** Clear token and notify app (on 401) */
const clearAuthAndNotify = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(REFRESH_KEY);
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
};

/**
 * Attempt to refresh the access token. Returns true on success.
 */
const tryRefreshToken = async (rt) => {
  if (isRefreshing) {
    // Wait for the existing refresh to complete
    try {
      await refreshPromise;
      return !!localStorage.getItem(AUTH_STORAGE_KEY);
    } catch {
      return false;
    }
  }
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const url = `${API_BASE_URL}/auth/refresh`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) throw new Error("Refresh failed");
      const data = await res.json();
      localStorage.setItem(AUTH_STORAGE_KEY, data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem(REFRESH_KEY, data.refreshToken);
      }
      return true;
    } catch (e) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(REFRESH_KEY);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

/**
 * Generic API request function
 */
const apiRequest = async (endpoint, options = {}) => {
  const method = options.method || "GET";
  const path = withPublicContentLangQuery(endpoint, method);
  const url = `${API_BASE_URL}${path}`;

  // Get auth token if available
  const token = getAuthToken();

  const config = {
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": getContentLang(),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  // Add body if it exists
  if (options.body && typeof options.body === "object") {
    config.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, config);
    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) {
      const text = await response.text();
      throw new Error(
        `API returned ${response.status} (expected JSON, got ${contentType.slice(0, 30)}). Check REACT_APP_API_URL points to backend.`
      );
    }

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh the token if we have a refresh token
        const rt = localStorage.getItem(REFRESH_KEY);
        if (rt && !endpoint.includes("/auth/refresh") && !endpoint.includes("/auth/logout")) {
          const refreshed = await tryRefreshToken(rt);
          if (refreshed) {
            // Retry the original request with the new token
            const newToken = localStorage.getItem(AUTH_STORAGE_KEY);
            const retryConfig = {
              ...config,
              headers: {
                ...config.headers,
                Authorization: `Bearer ${newToken}`,
              },
            };
            const retryResponse = await fetch(url, retryConfig);
            const retryData = await retryResponse.json();
            if (!retryResponse.ok) {
              const msg = retryData?.error?.message ?? retryData?.message ?? `HTTP error! status: ${retryResponse.status}`;
              throw new Error(msg);
            }
            return retryData;
          }
        }
        clearAuthAndNotify();
      }
      const msg = data?.error?.message ?? data?.message ?? `HTTP error! status: ${response.status}`;
      throw new Error(msg);
    }

    return data;
  } catch (error) {
    console.error("API request error:", error);
    throw error;
  }
};

/**
 * GET request
 */
export const get = (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    method: "GET",
    ...options,
  });
};

/**
 * POST request
 */
export const post = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    method: "POST",
    body,
    ...options,
  });
};

/**
 * PUT request
 */
export const put = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    method: "PUT",
    body,
    ...options,
  });
};

/**
 * DELETE request
 */
export const del = (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    method: "DELETE",
    ...options,
  });
};

/**
 * PATCH request
 */
export const patch = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    method: "PATCH",
    body,
    ...options,
  });
};

export const getBaseUrl = () => API_BASE_URL;
export default { get, post, put, patch, del, getBaseUrl };
